import React from "react";
import { DataView } from "primereact/dataview";
import { Chart } from "primereact/chart";
import { useGetEventCancellationsQuery } from "../../store/services/events";
import { EventCancellation } from "../../models/events";

interface EventCancelledDVProps {
  eventId: number;
}

export const EventCancelledDV = ({ eventId }: EventCancelledDVProps) => {
  const { data: cancellationsData, isLoading } =
    useGetEventCancellationsQuery(eventId);
    
  if (!isLoading && (!cancellationsData || cancellationsData.cancellations.length === 0)) {
    return null;
  }

  const prepareChartData = () => {
    if (!cancellationsData) return null;

    const reasonCounts: { [key: string]: number } = {};

    cancellationsData.cancellations.forEach((cancellation) => {
      const reasonText = cancellation.reason?.text || cancellation.customReason || "Sin razón especificada";
      reasonCounts[reasonText] = (reasonCounts[reasonText] || 0) + 1;
    });

    const labels = Object.keys(reasonCounts);
    const data = Object.values(reasonCounts);
    const total = cancellationsData.cancellations.length;

    const backgroundColors = [
      "#FF6384",
      "#36A2EB",
      "#FFCE56",
      "#4BC0C0",
      "#9966FF",
      "#FF9F40",
      "#FF6384",
      "#C9CBCF",
      "#4BC0C0",
      "#FF6384",
    ];

    return {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          hoverBackgroundColor: backgroundColors.slice(0, labels.length),
        },
      ],
      total,
    };
  };

  const chartData = prepareChartData();

  const chartOptions = {
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = chartData?.total || 0;
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="mt-4">
      <DataView
        header={
          <div className="flex align-items-center gap-2">
            <h1 className="text-3xl">Cancelaciones del evento</h1>
          </div>
        }
        loading={isLoading}
        value={cancellationsData?.cancellations || []}
        paginator
        rows={5}
        emptyMessage="Sin cancelaciones"
        listTemplate={(cancellations: EventCancellation[]): React.ReactNode[] =>
          cancellations.map((cancellation) => (
            <div
              className="p-3 border-bottom-1 surface-border"
              key={cancellation.id}
            >
              <div className="flex justify-content-between align-items-start">
                <div>
                  <h1 className="text-2xl font-medium text-900 p-0 m-0">
                    {cancellation.user.firstName} {cancellation.user.lastName}
                  </h1>
                  <p className="text-sm text-500 m-0 p-0">
                    {cancellation.user.email}
                  </p>
                  <p className="text-sm text-400 font-semibold m-0 p-0 mt-2">
                    Cancelado: {new Date(cancellation.cancelledAt).toLocaleString()}
                  </p>
                </div>
              </div>
              {(cancellation.reason || cancellation.customReason) && (
                <div className="mt-3 p-2 border-1 border-round border-300 bg-gray-50">
                  <p className="text-sm font-semibold text-700 m-0 mb-1">
                    Razón de cancelación:
                  </p>
                  <p className="text-base text-600 m-0">
                    {cancellation.reason?.text || cancellation.customReason}
                  </p>
                </div>
              )}
            </div>
          ))
        }
      />

      {chartData && chartData.datasets[0].data.length > 0 && (
        <div className="mt-4 p-4 border-1 border-round border-300">
          <h2 className="text-2xl mb-3">Distribución de razones de cancelación</h2>
          <div className="flex justify-content-center">
            <Chart
              type="pie"
              data={chartData}
              options={chartOptions}
              style={{ width: "100%", maxWidth: "150px" }}
            />
          </div>
          <div className="mt-3 text-center">
            <p className="text-lg font-semibold text-700">
              Total de cancelaciones: {cancellationsData?.cancellations.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
